import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { getApiConfigStatus } from '@/api/client'
import { shouldRetryQuery } from '@/lib/queryRetry'
import { ConfigError } from '@/components/config-error'
import { MainLayout } from '@/components/layout/main-layout'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { CalendarPage } from '@/pages/calendar'
import { InboxPage } from '@/pages/inbox'
import { PropertiesPage } from '@/pages/properties'
import { OnboardingPage } from '@/pages/onboarding'
import { PropertyDetailsPage } from '@/pages/property-details'
import { PropertyMessagesPage } from '@/pages/property-messages'
import { ReservationDetailsPage } from '@/pages/reservation-details'
import { ThreadDetailsPage } from '@/pages/thread-details'
import { OperationsPage } from '@/pages/operations'
import { UsersPage } from '@/pages/users'
import { UserDetailsPage } from '@/pages/user-details'
import { AssetsPage } from '@/pages/assets'
import { NewslettersPage } from '@/pages/newsletters'
import { ProtectionPage } from '@/pages/protection'
import { ReviewsPage } from '@/pages/reviews'
import { OrphanOffersPage } from '@/pages/orphan-offers'
import { CleaningPage } from '@/pages/cleaning'
import { TaxReportsPage } from '@/pages/tax-reports'
import { SuppliesPage } from '@/pages/supplies'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
  },
})

function AppRoutes() {
  return (
    <Routes>
      <Route element={<MainLayout />}>
        <Route path="/" element={<CalendarPage />} />
        {/* The dashboard gave way to the portfolio calendar (CAP-1415); old
            bookmarks land on it. */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/inbox/:threadId" element={<InboxPage />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/properties/:propertyId" element={<PropertyDetailsPage />} />
        <Route path="/properties/:propertyId/messages" element={<PropertyMessagesPage />} />
        <Route
          path="/properties/:propertyId/messages/:threadId"
          element={<PropertyMessagesPage />}
        />
        <Route path="/reservations/:reservationId" element={<ReservationDetailsPage />} />
        <Route path="/threads/:threadId" element={<ThreadDetailsPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/:userId" element={<UserDetailsPage />} />
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/settings" element={<Navigate to="/operations" replace />} />
        <Route path="/newsletters" element={<NewslettersPage />} />
        <Route path="/protection" element={<ProtectionPage />} />
        {/* Stripe Logs (formerly Stripe Alerts) became the Protection page's
            Activity tab (CAP-651); existing bookmarks keep working. */}
        <Route path="/stripe-logs" element={<Navigate to="/protection?tab=activity" replace />} />
        <Route path="/stripe-alerts" element={<Navigate to="/protection?tab=activity" replace />} />
        {/* Rankings feature removed 2026-09; keep bookmarks working. */}
        <Route path="/rankings" element={<Navigate to="/properties" replace />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/review-chase" element={<Navigate to="/reviews" replace />} />
        <Route path="/orphan-offers" element={<OrphanOffersPage />} />
        <Route path="/cleaning" element={<CleaningPage />} />
        <Route path="/tax-reports" element={<TaxReportsPage />} />
        <Route path="/supplies" element={<SuppliesPage />} />
      </Route>
    </Routes>
  )
}

function App() {
  // Show full-screen error only if completely unconfigured (no base URL)
  // Otherwise, let the app render and show a warning banner
  const configStatus = getApiConfigStatus()
  if (!import.meta.env.VITE_API_BASE_URL) {
    return <ConfigError missing={configStatus.missing} />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  )
}

export default App
